import React, { useState, useEffect, useCallback } from 'react';
import { DATABASE_API_URL, BACKEND_API_URL } from '../../apiConfig';
import Navigation from '../Navigation/Navigation';
import SavedStrategies from './SavedStrategies';
import UpstoxTokenStatus from '../../components/UpstoxTokenStatus';
import { refreshUpstoxToken } from '../../services/upstoxAuth';

export default function Options() {
    const [niftyData, setNiftyData] = useState(null);
    const [optionChainData, setOptionChainData] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [, setError] = useState(null);
    const [, setOptionsError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [selectedExpiry, setSelectedExpiry] = useState(null);
    const [availableExpirations, setAvailableExpirations] = useState([]);

    const handleAuthenticated = useCallback((authenticated) => {
        setIsAuthenticated(authenticated);
    }, []);
    const [expandedStrikes, setExpandedStrikes] = useState(new Set());

    const [manualStrategyDate, setManualStrategyDate] = useState(new Date().toISOString().split('T')[0]);
    const [manualBuyCall, setManualBuyCall] = useState('');
    const [manualBuyPut, setManualBuyPut] = useState('');
    const [manualSellCall, setManualSellCall] = useState('');
    const [manualSellPut, setManualSellPut] = useState('');
    const [manualBuyCallPremium, setManualBuyCallPremium] = useState('');
    const [manualBuyPutPremium, setManualBuyPutPremium] = useState('');
    const [manualSellCallPremium, setManualSellCallPremium] = useState('');
    const [manualSellPutPremium, setManualSellPutPremium] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingWeekly, setIsSavingWeekly] = useState(false);
    const [refreshStrategies, setRefreshStrategies] = useState(0);

    const handleManualBuyCallChange = (e) => {
        const val = e.target.value;
        setManualBuyCall(val);
        if (val) {
            const prem = calculatePremium(niftyData?.price, Number(val), 'buyCall', true);
            setManualBuyCallPremium(prem.toFixed(2));
        } else {
            setManualBuyCallPremium('');
        }
    };

    const handleManualBuyPutChange = (e) => {
        const val = e.target.value;
        setManualBuyPut(val);
        if (val) {
            const prem = calculatePremium(niftyData?.price, Number(val), 'buyPut', true);
            setManualBuyPutPremium(prem.toFixed(2));
        } else {
            setManualBuyPutPremium('');
        }
    };

    const handleManualSellCallChange = (e) => {
        const val = e.target.value;
        setManualSellCall(val);
        if (val) {
            const prem = calculatePremium(niftyData?.price, Number(val), 'sellCall', true);
            setManualSellCallPremium(prem.toFixed(2));
        } else {
            setManualSellCallPremium('');
        }
    };

    const handleManualSellPutChange = (e) => {
        const val = e.target.value;
        setManualSellPut(val);
        if (val) {
            const prem = calculatePremium(niftyData?.price, Number(val), 'sellPut', true);
            setManualSellPutPremium(prem.toFixed(2));
        } else {
            setManualSellPutPremium('');
        }
    };

    const handleRefreshPremiums = async () => {
        const data = await fetchOptionChain(selectedExpiry);
        if (data && data.chains) {
            const chains = data.chains;
            if (manualBuyCall) {
                const prem = calculatePremium(niftyData?.price, Number(manualBuyCall), 'buyCall', true, chains);
                setManualBuyCallPremium(prem.toFixed(2));
            }
            if (manualBuyPut) {
                const prem = calculatePremium(niftyData?.price, Number(manualBuyPut), 'buyPut', true, chains);
                setManualBuyPutPremium(prem.toFixed(2));
            }
            if (manualSellCall) {
                const prem = calculatePremium(niftyData?.price, Number(manualSellCall), 'sellCall', true, chains);
                setManualSellCallPremium(prem.toFixed(2));
            }
            if (manualSellPut) {
                const prem = calculatePremium(niftyData?.price, Number(manualSellPut), 'sellPut', true, chains);
                setManualSellPutPremium(prem.toFixed(2));
            }
        }
    };

    // Auto-save tracking with deduplication
    const [autoSaveNotification, setAutoSaveNotification] = useState(null);
    const [recentlySavedSignatures, setRecentlySavedSignatures] = useState(new Set());

    const getItmStatus = (spotPrice, strikes = {}) => {
        const price = Number(spotPrice);
        if (!price || !isFinite(price)) return false;

        const buyCall = Number(strikes.buyCall);
        const sellCall = Number(strikes.sellCall);
        const buyPut = Number(strikes.buyPut);
        const sellPut = Number(strikes.sellPut);

        return (
            (buyCall && buyCall <= price) ||
            (sellCall && sellCall <= price) ||
            (buyPut && buyPut >= price) ||
            (sellPut && sellPut >= price)
        );
    };

    const getSignalFromRatio = (rewardRatio, isITM = false) => {
        const ratio = Number(rewardRatio);

        if (isITM || !isFinite(ratio) || ratio < 0.4) {
            return { signal: 'AVOID', label: 'Avoid trade (ratio < 0.4 or ITM strikes)' };
        }
        if (ratio >= 0.8) {
            return { signal: 'STRONG BUY', label: 'Reward >= 0.8 x Risk' };
        }
        if (ratio >= 0.6) {
            return { signal: 'BUY', label: 'Reward >= 0.6 x Risk and < 0.8 x Risk' };
        }
        return { signal: 'NEUTRAL', label: 'Reward >= 0.4 x Risk and < 0.6 x Risk' };
    };

    // Function to fetch Nifty current price (non-blocking)
    const fetchNiftyPrice = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_API_URL}/api/upstox/nifty`);
            if (!response.ok) {
                const errorMsg = response.status === 401
                    ? 'Saved Upstox token is expired or invalid. Please generate a new token.'
                    : 'Failed to fetch Nifty 50 price';
                console.warn('Nifty price fetch error:', errorMsg);
                if (response.status === 401) {
                    setIsAuthenticated(false);
                }
                setError(errorMsg);
                return null;
            }
            const data = await response.json();
            setNiftyData(data);
            return data;
        } catch (err) {
            console.warn('Error fetching Nifty price:', err);
            setError(err.message);
            return null;
        }
    }, []);

    // Function to fetch historical data for strategy table simulation
    const fetchHistoricalData = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_API_URL}/api/upstox/historical`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbols: ['^NSEI'] }),
            });
            if (response.ok) {
                const result = await response.json();
                setHistoricalData(result.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch historical data', err);
        }
    }, []);

    // Function to fetch option chain data
    const fetchOptionChain = useCallback(async (expiryDate) => {
        try {
            setIsLoading(true);
            setError(null);
            setOptionsError(null);

            const params = new URLSearchParams();
            params.append('symbol', 'NSE_INDEX|Nifty 50');
            if (expiryDate) {
                params.append('expiryDate', expiryDate);
            }

            const response = await fetch(`${BACKEND_API_URL}/api/upstox/option-chain?${params}`);

            if (!response.ok) {
                if (response.status === 401) {
                    setIsAuthenticated(false);
                }
                console.warn(`Option chain: HTTP ${response.status} ${response.statusText}`);
                setOptionsError(response.status === 401
                    ? 'Saved Upstox token is expired or invalid. Please generate a new token.'
                    : `Failed to fetch option chain: ${response.statusText}`);
                return null;
            }

            const data = await response.json();

            if (data.status === 'success') {
                setOptionChainData(data);
                setSelectedExpiry(expiryDate);
                setError(null);
                return data;
            } else {
                console.warn('Option chain: API returned error status', data.error);
                setOptionsError(data.error || 'Failed to fetch option chain');
                return null;
            }
        } catch (err) {
            console.warn('Error fetching option chain:', err.message);
            setOptionsError(err.message || 'Failed to fetch option chain');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fallback to Next Tuesday specifically (Nifty Expiry)
    const getNextTuesday = useCallback(() => {
        const today = new Date();
        const daysUntilTuesday = (2 - today.getDay() + 7) % 7;
        const nextTuesday = new Date(today);
        nextTuesday.setDate(today.getDate() + daysUntilTuesday);
        return nextTuesday.toISOString().split('T')[0];
    }, []);

    // Function to fetch available expirations
    const fetchExpirations = useCallback(async () => {
        try {
            const response = await fetch(`${BACKEND_API_URL}/api/upstox/option-expirations`);

            if (!response.ok) {
                if (response.status === 401) {
                    setIsAuthenticated(false);
                }
                console.warn(`Expirations: HTTP ${response.status} error`);
                return null;
            }

            const data = await response.json();
            let expList = [];
            if (Array.isArray(data)) {
                expList = data;
            } else if (data && Array.isArray(data.data)) {
                expList = data.data;
            }

            // Extract strings if they are objects
            if (expList.length > 0 && typeof expList[0] === 'object' && expList[0].expiry) {
                expList = expList.map(e => e.expiry);
            }

            if (expList.length > 0) {
                setAvailableExpirations(expList);
                return expList[0]; // Return nearest expiry
            } else {
                // Fallback to next Tuesday if API returns empty
                const fallbackExpiry = getNextTuesday();
                setAvailableExpirations([fallbackExpiry]);
                return fallbackExpiry;
            }
        } catch (err) {
            console.warn('Error fetching expirations:', err.message);
            // Fallback to next Tuesday if API fails
            const fallbackExpiry = getNextTuesday();
            setAvailableExpirations([fallbackExpiry]);
            return fallbackExpiry;
        }
    }, [getNextTuesday]);

    // token generation handled by UpstoxTokenStatus component; helper removed

    // Function to check authentication status
    const checkAuthStatus = async () => {
        try {
            const status = await refreshUpstoxToken();
            setIsAuthenticated(status.authenticated);
            return status.authenticated;
        } catch (err) {
            console.error('Error checking auth status:', err);
            setIsAuthenticated(false);
            return false;
        }
    };

    // Corrected Weekly Options Strategy with OTM Validation
    const strategyStrikes = React.useMemo(() => {
        if (!niftyData?.price || !optionChainData?.chains) return null;

        const spotPrice = niftyData.price;
        const chains = optionChainData.chains;
        const availableStrikes = chains.map(c => c.strike_price).sort((a, b) => a - b);

        if (availableStrikes.length < 4) {
            return {
                status: 'error',
                message: 'Not enough strikes available',
                buyCall: null, buyPut: null, sellCall: null, sellPut: null,
                found: false
            };
        }

        const LOT_SIZE = 50; // Standard lot size for Nifty
        const SPREAD_WIDTH = 100; // Fixed 100-point spread

        let bestCombination = null;
        let bestRatio = 0;

        // Helper function to get premium from chain
        const getPremium = (strike, optionType) => {
            const chain = chains.find(c => c.strike_price === strike);
            if (!chain) return 0;
            const opt = optionType === 'CE' ? chain.call_options : chain.put_options;
            return (opt?.ltp || opt?.close_price || 0.05);
        };

        // OTM VALIDATION: Sell Call must be > spotPrice, Sell Put must be < spotPrice
        // Dynamic 1% OTM Buffer
        const otmBuffer = spotPrice * 0.01; // 1% buffer
        const minSellCall = spotPrice + otmBuffer; // At least 1% above spot
        const maxSellPut = spotPrice - otmBuffer; // At least 1% below spot

        // Get valid OTM sell strikes
        const validSellCallStrikes = availableStrikes.filter(s => s >= minSellCall);
        const validSellPutStrikes = availableStrikes.filter(s => s <= maxSellPut);

        if (validSellCallStrikes.length === 0 || validSellPutStrikes.length === 0) {
            return {
                status: 'no-strategy',
                message: '❌ Insufficient OTM strikes available (ITM strikes detected)',
                buyCall: null,
                buyPut: null,
                sellCall: null,
                sellPut: null,
                found: false
            };
        }

        // Try combinations with fixed 100-point spreads
        for (const sellCall of validSellCallStrikes) {
            const buyCall = sellCall + SPREAD_WIDTH;

            // Check if buyCall exists in available strikes
            if (!availableStrikes.includes(buyCall)) continue;

            for (const sellPut of validSellPutStrikes) {
                const buyPut = sellPut - SPREAD_WIDTH;

                // Check if buyPut exists in available strikes
                if (!availableStrikes.includes(buyPut)) continue;

                // SANITY CHECK: Ensure sells are OTM and buys are hedges
                if (sellCall <= spotPrice || sellPut >= spotPrice) {
                    console.warn(`[Strategy] Invalid OTM: sellCall=${sellCall}, sellPut=${sellPut}, spot=${spotPrice}`);
                    continue;
                }

                // Get premiums for this combination
                const sellCallPrem = getPremium(sellCall, 'CE');
                const buyCallPrem = getPremium(buyCall, 'CE');
                const sellPutPrem = getPremium(sellPut, 'PE');
                const buyPutPrem = getPremium(buyPut, 'PE');

                // CORRECTED P&L FORMULA
                const netCredit = (sellCallPrem + sellPutPrem) - (buyCallPrem + buyPutPrem);

                if (netCredit <= 0) continue; // Need positive credit

                // MaxLoss = (SpreadWidth - NetCredit) * LotSize
                const maxLoss = (SPREAD_WIDTH - netCredit) * LOT_SIZE;
                const maxProfit = netCredit * LOT_SIZE;

                // SANITY CHECK: MaxLoss must be positive and reasonable
                if (maxLoss <= 0 || !isFinite(maxLoss)) {
                    console.warn(`[Strategy] Invalid MaxLoss: ${maxLoss} for spread=${SPREAD_WIDTH}, credit=${netCredit}`);
                    continue;
                }

                if (maxProfit > 0 && maxLoss > 0) {
                    const ratio = maxProfit / maxLoss;

                    // Keep the best valid setup that clears the minimum neutral threshold.
                    if (ratio >= 0.4 && ratio > bestRatio) {
                        bestRatio = ratio;
                        bestCombination = {
                            buyCall,
                            buyPut,
                            sellCall,
                            sellPut,
                            ratio: parseFloat(ratio.toFixed(2)), // Direct ratio (for 1:X display)
                            found: true,
                            netCredit: parseFloat(netCredit.toFixed(2)),
                            maxProfit: parseFloat(maxProfit.toFixed(2)),
                            maxLoss: parseFloat(maxLoss.toFixed(2)),
                            spreadWidth: SPREAD_WIDTH,
                            isOTM: true, // Explicitly mark as valid OTM
                            sellCallPrem: parseFloat(sellCallPrem.toFixed(2)),
                            buyCallPrem: parseFloat(buyCallPrem.toFixed(2)),
                            sellPutPrem: parseFloat(sellPutPrem.toFixed(2)),
                            buyPutPrem: parseFloat(buyPutPrem.toFixed(2))
                        };
                    }
                }
            }
        }

        // Return best combination or error state
        if (bestCombination) {
            // Additional validation before returning
            if (!bestCombination.isOTM || bestCombination.maxLoss <= 0) {
                return {
                    status: 'error',
                    message: '❌ Strategy failed sanity checks (ITM strikes or zero loss)',
                    buyCall: null,
                    buyPut: null,
                    sellCall: null,
                    sellPut: null,
                    found: false
                };
            }
            return bestCombination;
        }

        return {
            status: 'no-strategy',
            message: '❌ No valid OTM strategy found in current market',
            buyCall: null,
            buyPut: null,
            sellCall: null,
            sellPut: null,
            found: false
        };
    }, [niftyData, optionChainData]);

    // Iron Condor Strategy Finder
    // eslint-disable-next-line no-unused-vars
    const ironCondorLegs = React.useMemo(() => {
        if (!niftyData?.price || !optionChainData?.chains) return null;

        const spotPrice = niftyData.price;
        const chains = optionChainData.chains;
        const availableStrikes = chains.map(c => c.strike_price).sort((a, b) => a - b);

        if (availableStrikes.length < 4) {
            return {
                found: false,
                message: 'Not enough strikes available for Iron Condor'
            };
        }

        const LOT_SIZE = 65;
        let bestCombination = null;
        let bestRatio = 0;

        // Helper function to get premium from chain
        const getPremium = (strike, optionType) => {
            const chain = chains.find(c => c.strike_price === strike);
            if (!chain) return 0;
            const opt = optionType === 'CE' ? chain.call_options : chain.put_options;
            return (opt?.ltp || opt?.close_price || 0.05);
        };

        // OTM Thresholds (1% above/below spot)
        const sellCallThreshold = spotPrice * 1.01;  // At least 1% above spot
        const sellPutThreshold = spotPrice * 0.99;   // At least 1% below spot

        // Get OTM strikes
        const otmCallStrikes = availableStrikes.filter(s => s >= sellCallThreshold);
        const otmPutStrikes = availableStrikes.filter(s => s <= sellPutThreshold);

        if (otmCallStrikes.length === 0 || otmPutStrikes.length === 0) {
            return {
                found: false,
                message: 'Insufficient OTM strikes available'
            };
        }

        // Try different spread widths (100 or 150 points)
        const spreadWidths = [100, 150];

        for (const spreadWidth of spreadWidths) {
            // Try each OTM call strike as Sell Call
            for (const sellCall of otmCallStrikes) {
                const buyCall = sellCall + spreadWidth;

                // Check if buyCall exists in available strikes
                if (!availableStrikes.includes(buyCall)) continue;

                // Try each OTM put strike as Sell Put
                for (const sellPut of otmPutStrikes) {
                    const buyPut = sellPut - spreadWidth;

                    // Check if buyPut exists in available strikes
                    if (!availableStrikes.includes(buyPut)) continue;

                    // Get premiums for this combination
                    const sellCallPrem = getPremium(sellCall, 'CE');
                    const buyCallPrem = getPremium(buyCall, 'CE');
                    const sellPutPrem = getPremium(sellPut, 'PE');
                    const buyPutPrem = getPremium(buyPut, 'PE');

                    // Premium Balance Check: Both sell premiums should be approximately equal (±25%)
                    const premiumDiff = Math.abs(sellCallPrem - sellPutPrem);
                    const avgPremium = (sellCallPrem + sellPutPrem) / 2;
                    const premiumRatio = avgPremium > 0 ? premiumDiff / avgPremium : 1;

                    if (premiumRatio > 0.25) continue; // Premiums too unbalanced, skip

                    // Calculate net premium and P&L
                    const netPremium = (sellCallPrem + sellPutPrem) - (buyCallPrem + buyPutPrem);

                    if (netPremium <= 0) continue; // Need positive net premium

                    const maxProfit = netPremium * LOT_SIZE;
                    const maxLoss = (spreadWidth - netPremium) * LOT_SIZE;

                    if (maxLoss > 0 && maxProfit > 0) {
                        const ratio = maxProfit / maxLoss;

                        if (ratio >= 0.4 && ratio > bestRatio) {
                            bestRatio = ratio;
                            bestCombination = {
                                buyCall,
                                buyPut,
                                sellCall,
                                sellPut,
                                ratio: parseFloat(ratio.toFixed(2)),
                                found: true,
                                netPremium: parseFloat(netPremium.toFixed(2)),
                                maxProfit: parseFloat(maxProfit.toFixed(2)),
                                maxLoss: parseFloat(maxLoss.toFixed(2)),
                                spreadWidth: spreadWidth,
                                sellCallPrem: parseFloat(sellCallPrem.toFixed(2)),
                                sellPutPrem: parseFloat(sellPutPrem.toFixed(2))
                            };
                        }
                    }
                }
            }
        }

        if (bestCombination) {
            return bestCombination;
        }

        return {
            found: false,
            message: '❌ No viable Iron Condor found with current market conditions'
        };
    }, [niftyData, optionChainData]);

    // Initialize component
    useEffect(() => {
        const initializeComponent = async () => {
            try {
                const isAuth = await checkAuthStatus();

                if (isAuth) {
                    setIsAuthenticated(true);
                }

                setIsLoading(false);
            } catch (err) {
                console.error('Error initializing component:', err);
                setIsLoading(false);
            }
        };

        initializeComponent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        const loadAuthenticatedData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([fetchNiftyPrice(), fetchHistoricalData()]);

                let expiry = await fetchExpirations();
                if (!expiry) {
                    expiry = getNextTuesday();
                    setAvailableExpirations([expiry]);
                }
                setSelectedExpiry(expiry);
                await fetchOptionChain(expiry);
            } catch (err) {
                console.error('Error loading authenticated data:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadAuthenticatedData();
    }, [isAuthenticated, fetchNiftyPrice, fetchHistoricalData, fetchExpirations, fetchOptionChain, getNextTuesday]);

    // Auto-refresh only the Nifty price every 60 seconds to avoid full component/data refresh
    // (avoid re-fetching the entire option chain each minute — that causes heavy re-renders)
    useEffect(() => {
        if (!isAuthenticated) return;

        const autoRefreshInterval = setInterval(async () => {
            try {
                console.log('[Auto Refresh] Fetching only Nifty price...');

                // Fetch only the live Nifty price. Option chain will NOT be re-fetched here.
                await fetchNiftyPrice();

                // If auto-save conditions are met, attempt an auto-save (it will noop if requirements missing)
                setTimeout(() => {
                    autoSaveStrategy();
                }, 500);
            } catch (err) {
                console.error('[Auto Refresh] Error during auto-refresh:', err);
            }
        }, 60000); // 60 seconds = 1 minute

        return () => clearInterval(autoRefreshInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, selectedExpiry]);

    // Toggle strike expansion
    // eslint-disable-next-line no-unused-vars
    const toggleStrike = (strikePrice) => {
        const newExpanded = new Set(expandedStrikes);
        if (newExpanded.has(strikePrice)) {
            newExpanded.delete(strikePrice);
        } else {
            newExpanded.add(strikePrice);
        }
        setExpandedStrikes(newExpanded);
    };

    // Get trading days for the strategy table (Current Date Only)
    const getStrategyDateRange = () => {
        return [new Date()];
    };

    // Get base price using historical data for previous days
    const getBasePrice = (date, currentPrice, historicalPrices) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);

        if (checkDate.getTime() === today.getTime()) return currentPrice;

        if (checkDate < today) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const historicalPoint = historicalPrices.find(h => {
                const hDate = new Date(h.date);
                return hDate.toISOString().split('T')[0] === dateStr;
            });
            return historicalPoint ? historicalPoint.close : currentPrice;
        }
        return currentPrice;
    };

    // Calculate premium price (Historical Estimation + Live Fallback)
    const calculatePremium = (basePrice, strikePrice, type, isToday, chains = optionChainData?.chains) => {
        if (!basePrice || !strikePrice) return 0;

        let livePremium = null;

        // Always try to get real live data from the chain
        if (chains) {
            const chain = chains.find(c => c.strike_price === strikePrice);
            const opt = type.toLowerCase().includes('call') ? chain?.call_options : chain?.put_options;

            if (opt) {
                if (opt.ltp !== null && opt.ltp !== undefined && opt.ltp !== 0) {
                    livePremium = opt.ltp;
                } else if (opt.close_price !== null && opt.close_price !== undefined && opt.close_price !== 0) {
                    livePremium = opt.close_price;
                } else {
                    livePremium = 0.05; // deep OTM fallback tick
                }
            }
        }

        if (isToday && livePremium !== null) {
            return livePremium;
        }

        const isCall = type.toLowerCase().includes('call');
        const diff = isCall ? basePrice - strikePrice : strikePrice - basePrice;
        const intrinsic = Math.max(0, diff);

        if (livePremium !== null) {
            const currentSpot = niftyData?.price || basePrice;
            const liveDiff = isCall ? currentSpot - strikePrice : strikePrice - currentSpot;
            const liveIntrinsic = Math.max(0, liveDiff);
            const liveTimeValue = Math.max(0, livePremium - liveIntrinsic);

            return Math.max(0.05, intrinsic + liveTimeValue);
        }

        // Absolute fallback formula if API completely unavailable
        return Math.max(0.05, intrinsic + Math.max(0, (basePrice * 0.005) - (Math.abs(diff) * 0.2)));
    };

    // Format Greeks display
    // eslint-disable-next-line no-unused-vars
    const formatGreek = (value) => {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'number') {
            return Math.abs(value) > 100 ? value.toFixed(0) : value.toFixed(3);
        }
        return value;
    };

    // Format price display
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === 0) return '—';
        return `₹${price.toFixed(2)}`;
    };

    // Function to automatically save non-ITM strategies that classify as BUY or STRONG BUY
    const buildStrategySignature = (strategy) => [
        strategy.expiry_date || '',
        Number(strategy.buy_call_strike),
        Number(strategy.buy_put_strike),
        Number(strategy.sell_call_strike),
        Number(strategy.sell_put_strike)
    ].join('|');

    const rememberRecentlySavedSignature = (strategySignature) => {
        setRecentlySavedSignatures((prev) => {
            const next = new Set(prev);
            next.add(strategySignature);
            return next;
        });

        setTimeout(() => {
            setRecentlySavedSignatures((prev) => {
                const updated = new Set(prev);
                updated.delete(strategySignature);
                return updated;
            });
        }, 15 * 60 * 1000);
    };

    const handleStrategiesDeleted = (deletedStrategies) => {
        deletedStrategies.forEach((strategy) => {
            rememberRecentlySavedSignature(buildStrategySignature(strategy));
        });
        setRefreshStrategies((prev) => prev + 1);
    };

    const strategyExistsInDb = async (payload) => {
        const response = await fetch(`${DATABASE_API_URL}/api/strategy`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            throw new Error('Failed to check saved strategies');
        }

        const data = await response.json();
        const savedStrategies = data.data || [];
        const payloadSignature = buildStrategySignature(payload);

        return savedStrategies.some((strategy) => buildStrategySignature(strategy) === payloadSignature);
    };

    const autoSaveStrategy = async () => {
        try {
            // Only auto-save valid non-ITM strategies that classify as BUY or STRONG BUY.
            if (!strategyStrikes?.found || !niftyData?.price || !optionChainData?.chains) return;

            const spotPrice = niftyData.price;
            const buyCall = strategyStrikes.buyCall;
            const buyPut = strategyStrikes.buyPut;
            const sellCall = strategyStrikes.sellCall;
            const sellPut = strategyStrikes.sellPut;
            const ratio = strategyStrikes.ratio; // Already calculated by corrected algorithm
            const isITM = getItmStatus(spotPrice, { buyCall, buyPut, sellCall, sellPut });
            const { signal } = getSignalFromRatio(ratio, isITM);

            if (!['BUY', 'STRONG BUY'].includes(signal)) {
                console.log(`[Auto Save] Strategy ratio 1:${ratio} classified as ${signal}, skipping auto-save`);
                return;
            }

            // Create unique signature for this strategy combination
            const strategySignature = `${selectedExpiry || ''}|${buyCall}|${buyPut}|${sellCall}|${sellPut}`;

            // DEDUPLICATION: Check if this exact strategy was saved recently
            if (recentlySavedSignatures.has(strategySignature)) {
                console.log('[Auto Save] Duplicate strategy detected, skipping:', strategySignature);
                return; // Skip if same strikes already saved recently
            }

            // Use pre-calculated values from the algorithm (already corrected)
            const maxProfit = strategyStrikes.maxProfit;
            const maxLoss = strategyStrikes.maxLoss;
            const rewardRatio = ratio; // ratio is in format: 0.5, 1, 2, etc. Display as "1:X"

            const payload = {
                selected_date: new Date().toISOString().split('T')[0],
                expiry_date: selectedExpiry,
                buy_call_strike: buyCall,
                buy_call_premium: strategyStrikes.buyCallPrem,
                buy_put_strike: buyPut,
                buy_put_premium: strategyStrikes.buyPutPrem,
                sell_call_strike: sellCall,
                sell_call_premium: strategyStrikes.sellCallPrem,
                sell_put_strike: sellPut,
                sell_put_premium: strategyStrikes.sellPutPrem,
                max_profit: maxProfit,
                max_loss: maxLoss,
                risk_reward_ratio: rewardRatio,
                signal: signal
            };

            const alreadySaved = await strategyExistsInDb(payload);
            if (alreadySaved) {
                console.log('[Auto Save] Strategy already exists in DB, skipping:', strategySignature);
                rememberRecentlySavedSignature(strategySignature);
                return;
            }

            const response = await fetch(`${DATABASE_API_URL}/api/strategy/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseData = await response.json();

                // Check if backend detected duplicate
                if (responseData.isDuplicate) {
                    console.log(`[Auto Save] Duplicate detected by backend, skipping notification`);
                    return; // Don't show notification for duplicates
                }

                console.log(`[Auto Save] Strategy saved! Ratio: 1:${rewardRatio} - Signal: ${signal}`);

                // Add to recently saved signatures to prevent duplicates for next 15 minutes
                rememberRecentlySavedSignature(strategySignature);

                // Show notification
                setAutoSaveNotification({
                    type: 'success',
                    message: `✅ Strategy auto-saved! Risk:Reward = 1:${rewardRatio} (${signal})`,
                    ratio: rewardRatio,
                    signal: signal
                });

                // Trigger refresh of saved strategies list
                setRefreshStrategies(prev => prev + 1);

                // Auto-hide notification after 5 seconds
                setTimeout(() => setAutoSaveNotification(null), 5000);
            } else {
                console.error('[Auto Save] Failed to save strategy');
            }
        } catch (err) {
            console.error('[Auto Save] Error:', err);
        }
    };

    const saveWeeklyStrategy = async () => {
        setIsSavingWeekly(true);
        try {
            if (!strategyStrikes?.found || !niftyData?.price) {
                alert('No weekly strategy available to save.');
                return;
            }

            const isITM = getItmStatus(niftyData.price, {
                buyCall: strategyStrikes.buyCall,
                buyPut: strategyStrikes.buyPut,
                sellCall: strategyStrikes.sellCall,
                sellPut: strategyStrikes.sellPut
            });
            const { signal } = getSignalFromRatio(strategyStrikes.ratio, isITM);

            const payload = {
                selected_date: new Date().toISOString().split('T')[0],
                expiry_date: selectedExpiry,
                buy_call_strike: strategyStrikes.buyCall,
                buy_call_premium: strategyStrikes.buyCallPrem,
                buy_put_strike: strategyStrikes.buyPut,
                buy_put_premium: strategyStrikes.buyPutPrem,
                sell_call_strike: strategyStrikes.sellCall,
                sell_call_premium: strategyStrikes.sellCallPrem,
                sell_put_strike: strategyStrikes.sellPut,
                sell_put_premium: strategyStrikes.sellPutPrem,
                max_profit: strategyStrikes.maxProfit,
                max_loss: strategyStrikes.maxLoss,
                risk_reward_ratio: strategyStrikes.ratio,
                signal: signal
            };

            const alreadySaved = await strategyExistsInDb(payload);
            if (alreadySaved) {
                alert('This weekly strategy already exists in saved strategies.');
                return;
            }

            const response = await fetch(`${DATABASE_API_URL}/api/strategy/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseData = await response.json();
                if (responseData.isDuplicate) {
                    alert('This weekly strategy already exists in saved strategies.');
                    return;
                }

                alert('Weekly strategy saved successfully!');
                setRefreshStrategies(prev => prev + 1);
            } else {
                alert('Failed to save weekly strategy.');
            }
        } catch (err) {
            console.error('Error saving weekly strategy:', err);
            alert('Error saving weekly strategy.');
        } finally {
            setIsSavingWeekly(false);
        }
    };

    const saveStrategy = async () => {
        setIsSaving(true);
        try {
            const buyCalls = manualBuyCallPremium !== '' ? Number(manualBuyCallPremium) : calculatePremium(niftyData?.price, Number(manualBuyCall), 'buyCall', true);
            const buyPuts = manualBuyPutPremium !== '' ? Number(manualBuyPutPremium) : calculatePremium(niftyData?.price, Number(manualBuyPut), 'buyPut', true);
            const sellCalls = manualSellCallPremium !== '' ? Number(manualSellCallPremium) : calculatePremium(niftyData?.price, Number(manualSellCall), 'sellCall', true);
            const sellPuts = manualSellPutPremium !== '' ? Number(manualSellPutPremium) : calculatePremium(niftyData?.price, Number(manualSellPut), 'sellPut', true);

            const netPremium = (sellCalls + sellPuts) - (buyCalls + buyPuts);

            const callSpread = Number(manualBuyCall) - Number(manualSellCall);
            const putSpread = Number(manualSellPut) - Number(manualBuyPut);
            const maxSpreadWidth = Math.max(callSpread, putSpread);

            const LOT_SIZE = 65;
            const maxProfit = netPremium * LOT_SIZE;
            const maxLoss = (maxSpreadWidth - netPremium) * LOT_SIZE;

            const rewardRatio = maxLoss > 0 ? (maxProfit / maxLoss).toFixed(2) : '0.00';
            const isITM = getItmStatus(niftyData?.price, {
                buyCall: manualBuyCall,
                buyPut: manualBuyPut,
                sellCall: manualSellCall,
                sellPut: manualSellPut
            });
            const { signal } = getSignalFromRatio(rewardRatio, isITM);

            const payload = {
                selected_date: manualStrategyDate,
                expiry_date: selectedExpiry,
                buy_call_strike: Number(manualBuyCall),
                buy_call_premium: buyCalls,
                buy_put_strike: Number(manualBuyPut),
                buy_put_premium: buyPuts,
                sell_call_strike: Number(manualSellCall),
                sell_call_premium: sellCalls,
                sell_put_strike: Number(manualSellPut),
                sell_put_premium: sellPuts,
                max_profit: maxProfit,
                max_loss: maxLoss,
                risk_reward_ratio: parseFloat(rewardRatio),
                signal: signal
            };

            const alreadySaved = await strategyExistsInDb(payload);
            if (alreadySaved) {
                alert('This strategy already exists in saved strategies.');
                return;
            }

            const response = await fetch(`${DATABASE_API_URL}/api/strategy/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseData = await response.json();
                if (responseData.isDuplicate) {
                    alert('This strategy already exists in saved strategies.');
                    return;
                }

                alert('Strategy saved successfully!');
                // Trigger refresh of saved strategies list
                setRefreshStrategies(prev => prev + 1);
                // Clear form
                setManualBuyCall('');
                setManualBuyPut('');
                setManualSellCall('');
                setManualSellPut('');
                setManualBuyCallPremium('');
                setManualBuyPutPremium('');
                setManualSellCallPremium('');
                setManualSellPutPremium('');
            } else {
                alert('Failed to save strategy.');
            }
        } catch (err) {
            console.error('Error saving strategy:', err);
            alert('Error saving strategy.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0B1121] text-gray-100">
            <Navigation />

            {/* Auto-Save Notification */}
            {autoSaveNotification && (
                <div className="fixed top-4 right-4 z-50 animate-slide-in">
                    <div className={`backdrop-blur-xl rounded-lg p-4 border-2 shadow-2xl ${autoSaveNotification.type === 'success'
                            ? 'bg-green-900/80 border-green-500'
                            : 'bg-red-900/80 border-red-500'
                        }`}>
                        <div className="flex items-center space-x-3">
                            <div className="flex-1">
                                <p className={`font-bold ${autoSaveNotification.type === 'success'
                                        ? 'text-green-100'
                                        : 'text-red-100'
                                    }`}>
                                    {autoSaveNotification.message}
                                </p>
                                {autoSaveNotification.signal && (
                                    <p className={`text-sm mt-1 ${autoSaveNotification.signal === 'STRONG BUY'
                                            ? 'text-green-300'
                                            : autoSaveNotification.signal === 'BUY'
                                                ? 'text-emerald-300'
                                                : 'text-blue-300'
                                        }`}>
                                        Signal: {autoSaveNotification.signal}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold text-white mb-8">Options Chain</h1>

                <UpstoxTokenStatus onAuthenticated={handleAuthenticated} />

                {/* Removed the 'Failed to Load Market Data' fallback banner as requested. */}

                {/* Current Price Section */}
                {isAuthenticated && niftyData && (
                    <div className="bg-gray-800/50 backdrop-blur-xl rounded-lg p-6 border border-gray-700/50 mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">Nifty 50 Current Price</h2>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <p className="text-3xl font-bold text-green-400">
                                    ₹{niftyData.price?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div>
                                <p className={`text-lg ${niftyData.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {niftyData.change >= 0 ? '+' : ''}{niftyData.change?.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-400">Change</p>
                            </div>
                            <div>
                                <p className={`text-lg ${niftyData.changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {niftyData.changePercent >= 0 ? '+' : ''}{niftyData.changePercent?.toFixed(2)}%
                                </p>
                                <p className="text-sm text-gray-400">Change %</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* No Strategy Found Alert */}
                {isAuthenticated && !isLoading && optionChainData && strategyStrikes?.found === false && (
                    <div className="bg-red-900/50 backdrop-blur-xl rounded-lg p-8 border border-red-700/50 mb-8">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-red-100 mb-3">❌ No Viable Strategy Found</h2>
                            <p className="text-red-200 mb-2">
                                {strategyStrikes?.message || 'Unable to find a strike combination with a Risk:Reward ratio of 1:5+'}
                            </p>
                            <p className="text-red-300 text-sm mt-4">
                                💡 Tip: Check back later when market volatility changes or implied premiums increase.
                            </p>
                        </div>
                    </div>
                )}

                {/* Strategy Table - Only show if valid strategy found */}
                {isAuthenticated && !isLoading && optionChainData && strategyStrikes?.found === true && (
                    <div className="bg-gray-800/50 backdrop-blur-xl rounded-lg p-6 border border-gray-700/50 mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-white"> Weekly Options Strategy </h2>
                            {selectedExpiry && (
                                <span className="text-sm text-gray-300 bg-gray-700/50 px-3 py-1 rounded">
                                    Expiry: <span className="text-blue-400 font-semibold">{new Date(selectedExpiry).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' })}</span>
                                </span>
                            )}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-300">
                                <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                                    <tr>
                                        <th className="px-6 py-3">Date</th>
                                        <th className="px-6 py-3">
                                            Buy Call (Hedge)<br />
                                            <span className="text-green-400 text-sm normal-case">{strategyStrikes.buyCall.toLocaleString('en-IN')}</span>
                                        </th>
                                        <th className="px-6 py-3">
                                            Buy Put (Hedge)<br />
                                            <span className="text-red-400 text-sm normal-case">{strategyStrikes.buyPut.toLocaleString('en-IN')}</span>
                                        </th>
                                        <th className="px-6 py-3">
                                            Sell Call<br />
                                            <span className="text-blue-400 text-sm normal-case">{strategyStrikes.sellCall.toLocaleString('en-IN')}</span>
                                        </th>
                                        <th className="px-6 py-3">
                                            Sell Put<br />
                                            <span className="text-yellow-400 text-sm normal-case">{strategyStrikes.sellPut.toLocaleString('en-IN')}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getStrategyDateRange().map((date, index) => {
                                        const isToday = date.toDateString() === new Date().toDateString();
                                        const basePrice = getBasePrice(date, niftyData?.price, historicalData);

                                        return (
                                            <tr key={index} className={`border-b border-gray-700/50 ${isToday ? 'bg-gray-700/30' : 'hover:bg-gray-700/20'}`}>
                                                <td className="px-6 py-4 font-medium text-white">
                                                    {date.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                                                    {isToday && <span className="ml-2 text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded">TODAY</span>}
                                                </td>
                                                <td className="px-6 py-4 text-green-400 font-semibold">
                                                    {formatPrice(calculatePremium(basePrice, strategyStrikes.buyCall, 'buyCall', isToday))}
                                                </td>
                                                <td className="px-6 py-4 text-red-400 font-semibold">
                                                    {formatPrice(calculatePremium(basePrice, strategyStrikes.buyPut, 'buyPut', isToday))}
                                                </td>
                                                <td className="px-6 py-4 text-blue-400 font-semibold">
                                                    {formatPrice(calculatePremium(basePrice, strategyStrikes.sellCall, 'sellCall', isToday))}
                                                </td>
                                                <td className="px-6 py-4 text-yellow-400 font-semibold">
                                                    {formatPrice(calculatePremium(basePrice, strategyStrikes.sellPut, 'sellPut', isToday))}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* P&L Calculations */}
                        {getStrategyDateRange().map((date) => {
                            // Use PRE-CALCULATED values from corrected algorithm
                            const maxProfit = strategyStrikes.maxProfit;
                            const maxLoss = strategyStrikes.maxLoss;
                            const rewardRatio = strategyStrikes.ratio; // Already in 1:X format

                            const hasValidPremium = maxProfit > 0 && maxLoss > 0;
                            const isITM = getItmStatus(niftyData?.price, {
                                buyCall: strategyStrikes.buyCall,
                                buyPut: strategyStrikes.buyPut,
                                sellCall: strategyStrikes.sellCall,
                                sellPut: strategyStrikes.sellPut
                            });
                            const { signal, label } = getSignalFromRatio(rewardRatio, isITM);
                            const isStrongBuy = signal === 'STRONG BUY';
                            const isBuy = signal === 'BUY';
                            const isNeutral = signal === 'NEUTRAL';

                            return (
                                <div key="pnl">
                                    <div className="mt-6 grid grid-cols-2 gap-4">
                                        <div className="bg-green-900/30 rounded-lg p-4 border border-green-700/50">
                                            <p className="text-green-300 text-sm font-semibold mb-1">Maximum Profit</p>
                                            <p className="text-green-400 text-2xl font-bold">
                                                ₹{Math.max(0, maxProfit).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                        <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/50">
                                            <p className="text-red-300 text-sm font-semibold mb-1">Maximum Loss</p>
                                            <p className="text-red-400 text-2xl font-bold">
                                                ₹{Math.max(0, maxLoss).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700/50">
                                            <p className="text-purple-300 text-sm font-semibold mb-1">Risk to Reward Ratio</p>
                                            <p className="text-purple-400 text-2xl font-bold">
                                                1 : {rewardRatio}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-2">
                                                = Risk 1 to make {rewardRatio}
                                            </p>
                                        </div>
                                        {hasValidPremium ? (
                                            <div className={`rounded-lg p-4 border-2 ${isStrongBuy || isBuy ? 'animate-pulse ' : ''}${isStrongBuy || isBuy ? 'bg-green-900/60 border-green-500' : (isNeutral ? 'bg-yellow-900/60 border-yellow-500' : 'bg-red-900/60 border-red-500')}`}>
                                                <p className={`text-sm font-semibold mb-1 ${isStrongBuy || isBuy ? 'text-green-300' : (isNeutral ? 'text-yellow-300' : 'text-red-300')}`}>📈 Signal</p>
                                                <p className={`text-2xl font-bold ${isStrongBuy || isBuy ? 'text-green-400' : (isNeutral ? 'text-yellow-400' : 'text-red-400')}`}>
                                                    {signal}
                                                </p>
                                                <p className={`text-xs mt-2 ${isStrongBuy || isBuy ? 'text-green-300' : (isNeutral ? 'text-yellow-300' : 'text-red-300')}`}>
                                                    {label}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bg-red-900/60 rounded-lg p-4 border border-red-500 border-2">
                                                <p className="text-red-300 text-sm font-semibold mb-1">📉 Signal</p>
                                                <p className="text-red-400 text-2xl font-bold">AVOID</p>
                                                <p className="text-xs text-red-300 mt-2">Negative Premium / Spread inverted</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-6 flex justify-end">
                                        <button
                                            onClick={saveWeeklyStrategy}
                                            disabled={isSavingWeekly}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                                        >
                                            {isSavingWeekly ? 'Saving...' : 'Save Strategy'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                )}
                {isAuthenticated && !isLoading && optionChainData && (
                    <div className="bg-gray-800/50 backdrop-blur-xl rounded-lg p-6 border border-gray-700/50 mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-white">Manual Weekly Options Strategy</h2>
                            <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-300">Expiry:</label>
                                <select
                                    value={selectedExpiry || ''}
                                    onChange={(e) => {
                                        setManualBuyCall('');
                                        setManualBuyPut('');
                                        setManualSellCall('');
                                        setManualSellPut('');
                                        setManualBuyCallPremium('');
                                        setManualBuyPutPremium('');
                                        setManualSellCallPremium('');
                                        setManualSellPutPremium('');
                                        fetchOptionChain(e.target.value);
                                    }}
                                    className="bg-gray-700 text-white text-sm rounded px-2 py-1 outline-none border border-gray-600 hover:border-gray-500 cursor-pointer"
                                >
                                    {availableExpirations.length === 0 ? (
                                        <option value="">Loading expirations...</option>
                                    ) : (
                                        availableExpirations.map(exp => (
                                            <option key={exp} value={exp}>{new Date(exp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-300">
                                <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                                    <tr>
                                        <th className="px-6 py-3">Date</th>
                                        <th className="px-6 py-3">Buy Call (Hedge)</th>
                                        <th className="px-6 py-3">Buy Put (Hedge)</th>
                                        <th className="px-6 py-3">Sell Call (1% Above)</th>
                                        <th className="px-6 py-3">Sell Put (1% Below)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-gray-700/50 bg-gray-700/30">
                                        <td className="px-6 py-4">
                                            <input
                                                type="date"
                                                value={manualStrategyDate}
                                                onChange={(e) => setManualStrategyDate(e.target.value)}
                                                className="bg-gray-800 text-white text-sm rounded px-2 py-1 outline-none border border-gray-600"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <select value={manualBuyCall} onChange={handleManualBuyCallChange} className="bg-gray-800 text-white text-sm rounded px-2 py-1 w-full border border-gray-600">
                                                <option value="">Select Strike</option>
                                                {optionChainData.chains.map(c => <option key={`bc-${c.strike_price}`} value={c.strike_price}>{c.strike_price}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select value={manualBuyPut} onChange={handleManualBuyPutChange} className="bg-gray-800 text-white text-sm rounded px-2 py-1 w-full border border-gray-600">
                                                <option value="">Select Strike</option>
                                                {optionChainData.chains.map(c => <option key={`bp-${c.strike_price}`} value={c.strike_price}>{c.strike_price}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select value={manualSellCall} onChange={handleManualSellCallChange} className="bg-gray-800 text-white text-sm rounded px-2 py-1 w-full border border-gray-600">
                                                <option value="">Select Strike</option>
                                                {optionChainData.chains.map(c => <option key={`sc-${c.strike_price}`} value={c.strike_price}>{c.strike_price}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select value={manualSellPut} onChange={handleManualSellPutChange} className="bg-gray-800 text-white text-sm rounded px-2 py-1 w-full border border-gray-600">
                                                <option value="">Select Strike</option>
                                                {optionChainData.chains.map(c => <option key={`sp-${c.strike_price}`} value={c.strike_price}>{c.strike_price}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-gray-700/50">
                                        <td className="px-6 py-4 font-medium text-gray-400">Premiums</td>
                                        <td className="px-6 py-4 text-green-400 font-semibold">
                                            {manualBuyCall ? (
                                                <div className="flex items-center space-x-1">
                                                    <span>₹</span>
                                                    <input
                                                        type="number"
                                                        step="0.05"
                                                        value={manualBuyCallPremium}
                                                        onChange={(e) => setManualBuyCallPremium(e.target.value)}
                                                        className="bg-gray-800 text-green-400 text-sm rounded px-2 py-1 w-full border border-gray-600 font-semibold outline-none focus:border-green-500 no-spinner"
                                                    />
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="px-6 py-4 text-red-400 font-semibold">
                                            {manualBuyPut ? (
                                                <div className="flex items-center space-x-1">
                                                    <span>₹</span>
                                                    <input
                                                        type="number"
                                                        step="0.05"
                                                        value={manualBuyPutPremium}
                                                        onChange={(e) => setManualBuyPutPremium(e.target.value)}
                                                        className="bg-gray-800 text-red-400 text-sm rounded px-2 py-1 w-full border border-gray-600 font-semibold outline-none focus:border-red-500 no-spinner"
                                                    />
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="px-6 py-4 text-blue-400 font-semibold">
                                            {manualSellCall ? (
                                                <div className="flex items-center space-x-1">
                                                    <span>₹</span>
                                                    <input
                                                        type="number"
                                                        step="0.05"
                                                        value={manualSellCallPremium}
                                                        onChange={(e) => setManualSellCallPremium(e.target.value)}
                                                        className="bg-gray-800 text-blue-400 text-sm rounded px-2 py-1 w-full border border-gray-600 font-semibold outline-none focus:border-blue-500 no-spinner"
                                                    />
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="px-6 py-4 text-yellow-400 font-semibold">
                                            {manualSellPut ? (
                                                <div className="flex items-center space-x-1">
                                                    <span>₹</span>
                                                    <input
                                                        type="number"
                                                        step="0.05"
                                                        value={manualSellPutPremium}
                                                        onChange={(e) => setManualSellPutPremium(e.target.value)}
                                                        className="bg-gray-800 text-yellow-400 text-sm rounded px-2 py-1 w-full border border-gray-600 font-semibold outline-none focus:border-yellow-500 no-spinner"
                                                    />
                                                </div>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* P&L Calculations for Manual Strategy */}
                        {(() => {
                            if (!manualBuyCall || !manualBuyPut || !manualSellCall || !manualSellPut) return null;

                            const buyCalls = manualBuyCallPremium !== '' ? Number(manualBuyCallPremium) : calculatePremium(niftyData?.price, Number(manualBuyCall), 'buyCall', true);
                            const buyPuts = manualBuyPutPremium !== '' ? Number(manualBuyPutPremium) : calculatePremium(niftyData?.price, Number(manualBuyPut), 'buyPut', true);
                            const sellCalls = manualSellCallPremium !== '' ? Number(manualSellCallPremium) : calculatePremium(niftyData?.price, Number(manualSellCall), 'sellCall', true);
                            const sellPuts = manualSellPutPremium !== '' ? Number(manualSellPutPremium) : calculatePremium(niftyData?.price, Number(manualSellPut), 'sellPut', true);

                            const netPremium = (sellCalls + sellPuts) - (buyCalls + buyPuts);

                            const callSpread = Number(manualBuyCall) - Number(manualSellCall);
                            const putSpread = Number(manualSellPut) - Number(manualBuyPut);
                            const maxSpreadWidth = Math.max(callSpread, putSpread);

                            const LOT_SIZE = 65;
                            const maxProfit = netPremium * LOT_SIZE;
                            const maxLoss = (maxSpreadWidth - netPremium) * LOT_SIZE;

                            const rewardRatio = maxLoss > 0 ? (maxProfit / maxLoss).toFixed(2) : '0.00';
                            const isITM = getItmStatus(niftyData?.price, {
                                buyCall: manualBuyCall,
                                buyPut: manualBuyPut,
                                sellCall: manualSellCall,
                                sellPut: manualSellPut
                            });
                            const { signal, label } = getSignalFromRatio(rewardRatio, isITM);
                            const isStrongBuy = signal === 'STRONG BUY';
                            const isBuy = signal === 'BUY';
                            const isNeutral = signal === 'NEUTRAL';

                            let signalText = signal;
                            let signalColorClass = 'text-red-400';
                            let bgClass = 'bg-red-900/60 border-red-500';
                            if (isStrongBuy) {
                                signalText = 'STRONG BUY';
                                signalColorClass = 'text-green-400';
                                bgClass = 'bg-green-900/60 border-green-500 animate-pulse';
                            } else if (isBuy) {
                                signalText = 'BUY';
                                signalColorClass = 'text-green-300';
                                bgClass = 'bg-green-800/60 border-green-400 animate-pulse';
                            } else if (isNeutral) {
                                signalText = 'NEUTRAL';
                                signalColorClass = 'text-yellow-400';
                                bgClass = 'bg-yellow-900/60 border-yellow-500';
                            }

                            return (
                                <div className="mt-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-green-900/30 rounded-lg p-4 border border-green-700/50">
                                            <p className="text-green-300 text-sm font-semibold mb-1">Maximum Profit</p>
                                            <p className="text-green-400 text-2xl font-bold">
                                                ₹{Math.max(0, maxProfit).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                        <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/50">
                                            <p className="text-red-300 text-sm font-semibold mb-1">Maximum Loss</p>
                                            <p className="text-red-400 text-2xl font-bold">
                                                ₹{Math.max(0, maxLoss).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700/50">
                                            <p className="text-purple-300 text-sm font-semibold mb-1">Risk to Reward Ratio</p>
                                            <p className="text-purple-400 text-2xl font-bold">
                                                1 : {rewardRatio}
                                            </p>
                                        </div>
                                        <div className={`rounded-lg p-4 border-2 ${bgClass}`}>
                                            <p className={`text-sm font-semibold mb-1 ${isStrongBuy || isBuy ? 'text-green-300' : (isNeutral ? 'text-yellow-300' : 'text-red-300')}`}>
                                                {isStrongBuy || isBuy ? '📈 Signal' : (isNeutral ? '⚖ Signal' : '📉 Signal')}
                                            </p>
                                            <p className={`text-2xl font-bold ${signalColorClass}`}>
                                                {signalText}
                                            </p>
                                            <p className={`text-xs mt-2 ${isStrongBuy || isBuy ? 'text-green-300' : (isNeutral ? 'text-yellow-300' : 'text-red-300')}`}>
                                                {label}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-6 flex justify-end space-x-4">
                                        <button
                                            onClick={handleRefreshPremiums}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                                        >
                                            Refresh Premiums
                                        </button>
                                        <button
                                            onClick={saveStrategy}
                                            disabled={isSaving}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                        >
                                            {isSaving ? 'Saving...' : 'Save Strategy'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Saved Strategies Component - Below Manual Weekly Options Strategy */}
                <SavedStrategies
                    refreshTrigger={refreshStrategies}
                    optionChainData={optionChainData}
                    selectedExpiry={selectedExpiry}
                    onStrategiesDeleted={handleStrategiesDeleted}
                    historicalData={historicalData}
                    niftyData={niftyData}
                />
            </div>
        </div>
    );
}
